import React, {MutableRefObject, useContext, useEffect, useState} from 'react';
import Modal from 'react-modal';
import {useTypedFastStorage} from '../../../hooks/useFastStorage';

import {GeneralLedger} from "../../../ifmsTypes";
import InlineInputControlRow from "../../../ui/layout/InlineInputControlRow";
import DateControl from "../../../ui/Date";
import BranchDropDown from "../../../uiControls/BranchDropDown";
import SecondaryButton from "../../../ui/SecondaryButton";
import { compareIso, formatDate, getCurrentMonth, getCurrentYear, getDate, now } from '../../../dateUtils';
import moment from 'moment';
import Fieldset from '../../common/Fieldset';
import { GeneralLedgerArgs, NewGeneralJournalArgs } from '../../../fms.report/args';
import { DocumentType } from '../../../framework.core/DocumentType';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import useReportViewer from '../../../hooks/useReportViewer';
import ApplicationContext, { IApplicationContext } from '../../../ApplicationContext';
import config from '../../../config';
import Spinner from '../../../ui/Spinner';
import { getFmsGlobal } from '../../../globals';

interface GeneralLedgerStorageKeys {
  modelStorage: string,
};

interface GeneralLedgerViewParams {
  id: string | undefined,
  onPopupClose?: () => void,
  documentType: DocumentType
};

interface GeneralLedgerLoadedDetailsViewParams {
  generalLedger: GeneralLedger,
  generalLedgerRef: MutableRefObject<GeneralLedger | null>,
  setGeneralLedger: (x: Partial<GeneralLedger>) => void,
  onPopupClose?: () => void,
  documentType: DocumentType,
  appContext: IApplicationContext,
  startDate: string | undefined,
};

function getGeneralLedgerStorageKeys(): GeneralLedgerStorageKeys {
  return {modelStorage: 'report-general-ledger'}
}

function GeneralLedgerImpl(params: GeneralLedgerViewParams) {
  const fmsGlobal = getFmsGlobal();
  const {modelStorage} = getGeneralLedgerStorageKeys();
  const [inStorage, setInStorage, removeGeneralLedgerFromStorage, generalLedgerRef] = useTypedFastStorage<GeneralLedger>(modelStorage, null);
  const [initialized, setInitialized] = useState(false);
  const appContext = useContext(ApplicationContext);
  const acctOptions = appContext.suntekSettings?.accounting;
  const startDate = acctOptions?.accountingStartDateYear && acctOptions?.accountingStartDateMonth ? moment(new Date(
    Number(acctOptions?.accountingStartDateYear),
    Number(acctOptions?.accountingStartDateMonth)-1, 1)).format() : moment(new Date(2000, 0, 1)).format();
  const noBranchSelection = (appContext.systemSettings?.enableBranch && fmsGlobal.NoBranchSelection) ?? false;

  useEffect(() => {
      initialize();
      return () => { removeGeneralLedgerFromStorage() }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  async function loadExisting() {
    let fromDate = now();
    let toDate = now();
    const acctStartDate = getDate(startDate)??"";

    if(params.documentType === DocumentType.NewGeneralJournal){
      const currentMonth = getCurrentMonth();
      const currentYear = getCurrentYear();
      const fiscalStartMonth = acctOptions?.fiscalEndMonth === 12 ? 0 : acctOptions?.fiscalEndMonth||1 - 1;
      fromDate = moment(new Date(currentMonth > fiscalStartMonth ? currentYear : currentYear - 1, fiscalStartMonth, 1)).format()
    }
    if(compareIso(acctStartDate, fromDate) > 0){
      fromDate = acctStartDate;
    }
    if(compareIso(acctStartDate, toDate) > 0){
      toDate = acctStartDate;
    }

    return {fromDate, toDate, branch: noBranchSelection ? appContext.systemSettings?.userBranch ?? '' : 'ALL'}
  }

  async function initialize() {
    if (!inStorage) {
      const data = await loadExisting();
      setInStorage({...data})
    }
    setInitialized(true);
  }

  if (!initialized || !inStorage) {
    return null;
  }

  return (
    <GeneralLedgerInitializedImpl
      generalLedger={inStorage}
      generalLedgerRef={generalLedgerRef}
      setGeneralLedger={setInStorage}
      onPopupClose={params.onPopupClose}
      documentType={params.documentType}
      appContext={appContext}
      startDate={startDate}
    />
  );
}

function GeneralLedgerInitializedImpl({
                                        generalLedger,
                                        generalLedgerRef,
                                        setGeneralLedger,
                                        onPopupClose,
                                        documentType,
                                        appContext,
                                        startDate
                                      }: GeneralLedgerLoadedDetailsViewParams) {
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const { viewReport } = useReportViewer();
  const [exportLoadingMessage, setExportLoadingMessage] = useState(false);
    
  const ModalOverlayStyle:React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    zIndex: 999998
  };

  const ModalContentStyle:React.CSSProperties = {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 10000
  };
  
  function onChange(change: Partial<GeneralLedger>) {
    setGeneralLedger(change);
  }

  async function getReportArg(isExport: boolean): Promise<NewGeneralJournalArgs|GeneralLedgerArgs|null> {
    //#region CheckPrintOptions
    let errorMsg = '';

    const acctStartDate = getDate(startDate)??"";
    const fromDate = getDate(generalLedger.fromDate)??"";
    const toDate = getDate(generalLedger.toDate)??"";

    if(!fromDate){
      errorMsg = "Please enter valid starting date.";
    }else if(!toDate){
      errorMsg = "Please enter valid ending date.";
    }
    // else if(compareIso(acctStartDate, fromDate) > 0){
    //   errorMsg = `The starting date is smaller than the start date of new accounting.\n(The start date of new accounting is on ${formatDate(startDate)}`;
    // }else if(compareIso(acctStartDate, toDate) > 0){
    //   errorMsg = `The ending date is smaller than the start date of new accounting.\n(The start date of new accounting is on ${formatDate(startDate)}`;
    // }
    else if(compareIso(fromDate, toDate) > 0){
      errorMsg = "The ending date is smaller than the starting date.";
    }

    if(errorMsg) {
      await showGlobalOkDialog(<p>{errorMsg}</p>, false, null, "info");
      return null;
    }
    //#endregion

    let title;
    let args;
    if(documentType === DocumentType.NewGeneralJournal){
      title = `General Ledger: ${formatDate(fromDate)} ~ ${formatDate(toDate)}`;
      args = new NewGeneralJournalArgs({
        docType: DocumentType.NewGeneralJournal,
        formCaption: title,
        subject: title,
        startDate: fromDate,
        endingDate: toDate,
        branch: generalLedger.branch === 'ALL' ? '' : generalLedger.branch,
        isExportToExcel: isExport
      });
    }else{
      title = `Journal Book: ${formatDate(fromDate)} ~ ${formatDate(toDate)}`;
      args = new GeneralLedgerArgs({
        docType: DocumentType.GeneralLedger,
        formCaption: title,
        subject: title,
        dateFrom: fromDate,
        dateTo: toDate,
        branch: generalLedger.branch === 'ALL' ? '' : generalLedger.branch,
        isExportToExcel: isExport
      });
    }
    return args;
  }

  async function print() {
    const args = await getReportArg(false);
    if(args) {
      viewReport(args, "", true);
    }
  }

  async function excelExport() {
    setExportLoadingMessage(true);

    const args = await getReportArg(true);
    if(args) {
      const userToken = appContext.user ? appContext.user.accessToken ?? '' : '';
      try {
        const response = await fetch(`${config.serverUrl}report/exportreport`, {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ args: { docType: args.docType, reportArgs: args }, jwt:userToken})
        });
    
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = (args.subject+".xlsx").replaceAll(' ', '-');
          a.click();
        } else {
          console.error('error', response.status);
        }
      } catch (error) {
        console.error('error', error);
      } finally {
        setExportLoadingMessage(false);
      }
    }
  }

  return (
    <>
      <div className='report-popup'>
        <Fieldset label='Period'>
          <div style={{display: 'flex'}}>
            <div style={{marginLeft: '200px', width: '450px'}}>
              <InlineInputControlRow mode='w60' label="Date">
                <div style={{display: 'flex'}}>
                  <div style={{width: '80px'}}>
                    <DateControl name='journal-book-fromDate' value={generalLedger.fromDate} onChange={fromDate => onChange({fromDate})} />
                  </div>

                  <div style={{width: '60px', paddingLeft: '35px'}}>
                    ~
                  </div>

                  <div style={{width: '80px'}}>
                    <DateControl name='journal-book-toDate' value={generalLedger.toDate} onChange={toDate => onChange({toDate})} />
                  </div>

                  {/* <div style={{marginLeft: '40px', width: '130px'}}>
                    <CheckBox label='Show Foreign Currency' value={generalLedger.showForeignCurrency} onChange={showForeignCurrency => onChange({showForeignCurrency})} />
                  </div> */}
                </div>
              </InlineInputControlRow>  
            </div>
          </div>
        </Fieldset>

        {appContext.systemSettings?.enableBranch ? 
        <Fieldset label='Select Branch'>
          <div style={{display: 'flex'}}>
            <div style={{marginLeft: '260px', width: '160px'}}>
              <InlineInputControlRow mode='w60' label="Branch">
                <BranchDropDown isReport addAll disabled={false} value={generalLedger?.branch} onChange={branch => onChange({branch})} />
              </InlineInputControlRow>  
            </div>
          </div>
        </Fieldset> : null}
        
        <div className='report-footer'>
          {documentType === DocumentType.GeneralLedger ?
          <SecondaryButton
            size="regular"
            style={{width: '90px'}}
            onClick={() => { excelExport()}}>
            EXPORT
          </SecondaryButton> : null}
          <SecondaryButton
            size="regular"
            style={{width: '90px'}}
            onClick={() => {
              print();
            }}>
            PREVIEW
          </SecondaryButton>
          <SecondaryButton
            size="regular"
            style={{width: '90px'}}
            onClick={onPopupClose}>
            CANCEL
          </SecondaryButton>
        </div>
      </div>
      {exportLoadingMessage &&
        <Modal
          isOpen={exportLoadingMessage}
          shouldCloseOnOverlayClick={false}
          shouldCloseOnEsc={false}
          contentLabel=""
          style={{
            overlay: { ...ModalOverlayStyle, backgroundColor: 'rgba(0, 0, 0, 0.2)' },
            content: ModalContentStyle,
          }}
        >
          <div style={{ display: 'flex', height: '150px', width: '350px', justifyContent: 'center', alignItems: 'center', fontSize: '18px', }}>
            <Spinner /> Please wait while processing…
          </div>
        </Modal>
      }
    </>
  );
}

const GeneralLedgerDetails = ({
                                onPopupClose,
                                documentType
                              }
                                : { 
                                  onPopupClose?: () => void,
                                  documentType: DocumentType
                                }) => {
  // const { id } = useParams<GeneralLedgerViewParams>();

  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */

  return (
    <GeneralLedgerImpl id={''} documentType={documentType} onPopupClose={onPopupClose} key={`generalLedger-details`}/>
  );
};
export default GeneralLedgerDetails;
